import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
export const useDocumentManager = ({
  entityId,
  entityType,
  documents: propDocuments,
  setDocuments: setPropDocuments,
  isEditing,
  orgId,
  documentTypes,
  specificTypes,
  maxOtherDocuments,
  apiEndpoint,
  config,
  onSubmit,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState(
    propDocuments?.length > 0
      ? propDocuments
      : [
          {
            id: 1,
            type: "",
            file: null,
            fileName: "",
            preview: null,
            isExisting: false,
            isUploaded: false,
            needsReplacement: false,
          },
        ]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    documentId: null,
  });

  useEffect(() => {
    if (isEditing && entityId && orgId) {
      fetchExistingDocuments();
    }
    return () => {
      documents.forEach((doc) => {
        if (doc.preview && !doc.isExisting) {
          URL.revokeObjectURL(doc.preview);
        }
      });
    };
  }, [entityId, isEditing, orgId]);

  const filterMostRecentDocuments = (docs) => {
    const groupedDocs = docs.reduce((acc, doc) => {
      const type = doc.name || "Other";
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(doc);
      return acc;
    }, {});

    return Object.values(groupedDocs).map(
      (group) =>
        group.sort(
          (a, b) =>
            new Date(b.createdAt || b.updatedAt || "1970-01-01T00:00:00Z") -
            new Date(a.createdAt || a.updatedAt || "1970-01-01T00:00:00Z")
        )[0]
    );
  };

  const fetchExistingDocuments = async () => {
    setIsLoading(true);
    try {
      if (!entityId || isNaN(parseInt(entityId))) {
        setAlert({
          open: true,
          message: t(`Invalid ${entityType} ID provided`),
          severity: "error",
        });
        setDocuments([
          {
            id: 1,
            type: "",
            file: null,
            fileName: "",
            preview: null,
            isExisting: false,
            isUploaded: false,
            needsReplacement: false,
          },
        ]);
        setPropDocuments([]);
        return;
      }

      const response = await axios.get(`${config.baseURL}${apiEndpoint}`, {
        params: {
          [entityType === "Vehicle" ? "vehicleId" : "id"]: entityId,
          organizationId: orgId,
        },
        withCredentials: true,
      });

      const entityData =
        response.data[entityType.toLowerCase()] || response.data;
      let entityDocs = entityData.documents || [];

      // Filter out "VehicleImage" for vehicles and "DriverImage" for drivers
      const filteredDocs = filterMostRecentDocuments(
        entityDocs.filter((doc) =>
          entityType === "Vehicle"
            ? doc.name !== "VehicleImage"
            : doc.name !== "DriverImage"
        )
      );

      if (Array.isArray(filteredDocs) && filteredDocs.length > 0) {
        const existingDocs = filteredDocs.map((doc, index) => ({
          id: doc.id || index + 1,
          type: doc.name || "Other",
          fileName: doc.location ? doc.location.split("/").pop() : "",
          fileId: doc.id,
          isExisting: true,
          isUploaded: true,
          createdAt: doc.createdAt,
          updatedAt: doc.updatedAt,
          preview: doc.location
            ? `${config.baseURL}/upload/image?fileName=${encodeURIComponent(
                doc.location
              )}`
            : null,
          needsReplacement: false,
        }));

        setDocuments(existingDocs);
        setPropDocuments(existingDocs);
      } else {
        // setAlert({
        //     open: true,
        //     message: t(`No documents found for this ${entityType.toLowerCase()}`),
        //     severity: 'info',
        // });
        setDocuments([
          {
            id: 1,
            type: "",
            file: null,
            fileName: "",
            preview: null,
            isExisting: false,
            isUploaded: false,
            needsReplacement: false,
          },
        ]);
        setPropDocuments([]);
      }
    } catch (error) {
      console.error(`Error fetching ${entityType} documents:`, error);
      setAlert({
        open: true,
        message: t(
          `Failed to load existing documents: ${
            error.response?.data?.error || error.message
          }`
        ),
        severity: "error",
      });
      setDocuments([
        {
          id: 1,
          type: "",
          file: null,
          fileName: "",
          preview: null,
          isExisting: false,
          isUploaded: false,
          needsReplacement: false,
        },
      ]);
      setPropDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateDocument = (doc) => {
    const newErrors = {};
    if (!doc.type) {
      newErrors.type = "Document Type is required";
    }
    if (!doc.file && !doc.isUploaded) {
      newErrors.file = "Document File is required";
    }
    return newErrors;
  };

  const validateAllDocuments = () => {
    const newErrors = {};
    documents.forEach((doc) => {
      if (touched[doc.id]) {
        newErrors[doc.id] = validateDocument(doc);
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).every(
      (id) => Object.keys(newErrors[id]).length === 0
    );
  };

  useEffect(() => {
    validateAllDocuments();
  }, [documents, touched]);

  const getDocumentCounts = () => {
    const typeCounts = {};
    specificTypes.forEach((type) => {
      typeCounts[type] = 0;
    });
    typeCounts["Other"] = 0;

    documents.forEach((doc) => {
      if ((doc.isUploaded || doc.file) && doc.type) {
        typeCounts[doc.type] = (typeCounts[doc.type] || 0) + 1;
      }
    });

    return typeCounts;
  };

  const canAddDocument = () => {
    const typeCounts = getDocumentCounts();
    const otherCount = typeCounts["Other"] || 0;
    const hasRemainingOtherSlots = otherCount < maxOtherDocuments;
    const hasAvailableSpecificTypes = specificTypes.some(
      (type) => !typeCounts[type]
    );

    const hasDocumentWithoutType = documents.some(
      (doc) => (doc.file || doc.preview) && !doc.type
    );

    const hasTypeWithoutFile = documents.some(
      (doc) => doc.type && !doc.file && !doc.isUploaded && !doc.preview
    );

    const hasEmptyForm = documents.some(
      (doc) => !doc.type && !doc.file && !doc.isUploaded
    );

    return (
      (hasRemainingOtherSlots || hasAvailableSpecificTypes) &&
      !hasEmptyForm &&
      !hasDocumentWithoutType &&
      !hasTypeWithoutFile
    );
  };

  const getRemainingOtherDocs = () => {
    const totalUploadedDocs = documents.filter(
      (doc) => (doc.isUploaded || doc.file) && doc.type
    ).length;
    const totalAllowed = specificTypes.length + maxOtherDocuments;
    return totalAllowed - totalUploadedDocs;
  };

  const getAvailableDocTypes = (currentDocId) => {
    const doc = documents.find((d) => d.id === currentDocId);
    const usedSpecificTypes = documents
      .filter(
        (d) =>
          specificTypes.includes(d.type) &&
          (d.isUploaded || d.file) &&
          d.id !== currentDocId
      )
      .map((d) => d.type);

    const availableTypes = documentTypes.filter((type) => {
      if (type === "Other" || (doc && doc.type === type)) {
        return true;
      }
      return !usedSpecificTypes.includes(type);
    });

    return {
      availableTypes,
      usedTypes: usedSpecificTypes,
    };
  };

  const handleAddDocument = () => {
    if (!canAddDocument()) {
      setAlert({
        open: true,
        message: t("Cannot add more documents"),
        severity: "warning",
      });
      return;
    }
    const newId =
      documents.length > 0
        ? Math.max(...documents.map((doc) => doc.id)) + 1
        : 1;
    const newDoc = {
      id: newId,
      type: "",
      file: null,
      fileName: "",
      preview: null,
      isExisting: false,
      isUploaded: false,
      needsReplacement: false,
    };
    setDocuments([...documents, newDoc]);
    setPropDocuments((prev) => [...prev, newDoc]);
  };

  const handleRemoveDocument = (id) => {
    const docToRemove = documents.find((doc) => doc.id === id);

    if (docToRemove.isExisting) {
      setConfirmModal({
        open: true,
        documentId: id,
      });
      return;
    }

    removeDocumentById(id);
  };

  const handleCancelRemove = () => {
    setConfirmModal({ open: false, documentId: null });
  };

  const handleConfirmRemove = async () => {
    const docId = confirmModal.documentId;
    const docToRemove = documents.find((doc) => doc.id === docId);

    if (docToRemove.isExisting && docToRemove.fileId) {
      try {
        // Call the DELETE endpoint
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: docToRemove.type || "Other",
            associateEntityId: entityId,
            associatedWith: entityType,
          },
          withCredentials: true,
        });

        console.log(`Successfully deleted document: ${docToRemove.type}`);
      } catch (error) {
        console.error("Error deleting document:", error);
        setAlert({
          open: true,
          message: t(
            `Failed to delete document: ${
              error.response?.data?.error || error.message
            }`
          ),
          severity: "error",
        });
        return;
      }
    }

    removeDocumentById(docId);
    setConfirmModal({ open: false, documentId: null });
  };

  const removeDocumentById = (id) => {
    const docToRemove = documents.find((doc) => doc.id === id);

    if (docToRemove.preview && !docToRemove.isExisting) {
      URL.revokeObjectURL(docToRemove.preview);
    }

    const newDocuments = documents.filter((doc) => doc.id !== id);
    setDocuments(
      newDocuments.length > 0
        ? newDocuments
        : [
            {
              id: 1,
              type: "",
              file: null,
              fileName: "",
              preview: null,
              isExisting: false,
              isUploaded: false,
              needsReplacement: false,
            },
          ]
    );
    setPropDocuments(newDocuments.length > 0 ? newDocuments : []);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
    setTouched((prev) => {
      const newTouched = { ...prev };
      delete newTouched[id];
      return newTouched;
    });
  };

  const handleDocTypeChange = (id, value) => {
    const existingSpecificDoc = documents.find(
      (doc) =>
        doc.type === value &&
        specificTypes.includes(value) &&
        (doc.isUploaded || doc.file) &&
        doc.id !== id
    );

    if (existingSpecificDoc && specificTypes.includes(value)) {
      setAlert({
        open: true,
        message: t(`A ${value} document already exists`),
        severity: "warning",
      });
      return;
    }

    if (value === "Other") {
      const currentOtherCount = documents.filter(
        (doc) =>
          doc.type === "Other" && (doc.isUploaded || doc.file) && doc.id !== id
      ).length;

      if (currentOtherCount >= maxOtherDocuments) {
        setAlert({
          open: true,
          message: t(`Maximum ${maxOtherDocuments} Other documents allowed`),
          severity: "warning",
        });
        return;
      }
    }

    const updatedDocs = documents.map((doc) =>
      doc.id === id ? { ...doc, type: value } : doc
    );
    setDocuments(updatedDocs);
    setPropDocuments(updatedDocs.filter((doc) => doc.type !== ""));
    setTouched((prev) => ({
      ...prev,
      [id]: { ...prev[id], type: true },
    }));
    setErrors((prev) => ({
      ...prev,
      [id]: validateDocument({
        ...documents.find((doc) => doc.id === id),
        type: value,
      }),
    }));
  };
  const handleFileUpload = (id, event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const allowedFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];

      if (!allowedFileTypes.includes(file.type)) {
        setAlert({
          open: true,
          message: t(
            "Invalid file type. Only JPG, JPEG, PNG, and PDF are allowed."
          ),
          severity: "error",
        });
        setTouched((prev) => ({
          ...prev,
          [id]: { ...prev[id], file: true },
        }));
        setErrors((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            file: t(
              "Invalid file type. Only JPG, JPEG, PNG, and PDF are allowed."
            ),
          },
        }));
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setAlert({
          open: true,
          message: t("File size exceeds 10MB limit"),
          severity: "warning",
        });
        return;
      }

      const fileUrl = URL.createObjectURL(file);

      const updatedDocs = documents.map((doc) => {
        if (doc.id === id) {
          if (doc.preview && !doc.isExisting) {
            URL.revokeObjectURL(doc.preview);
          }
          return {
            ...doc,
            file,
            fileName: file.name,
            fileType: file.type,
            preview: fileUrl,
            isUploaded: false,
            needsReplacement: doc.isExisting ? true : false,
          };
        }
        return doc;
      });
      setDocuments(updatedDocs);
      setPropDocuments(updatedDocs.filter((doc) => doc.type !== ""));
      setTouched((prev) => ({
        ...prev,
        [id]: { ...prev[id], file: true },
      }));
      setErrors((prev) => ({
        ...prev,
        [id]: validateDocument({
          ...documents.find((doc) => doc.id === id),
          file,
          fileName: file.name,
          preview: fileUrl,
          isUploaded: false,
        }),
      }));

      event.target.value = "";
    }
  };

  const handleDownload = async (doc) => {
    if (doc.preview) {
      try {
        let blobUrl = doc.preview;
        let fileName = doc.fileName || "document";

        // If the preview is a server URL, fetch the file and create a Blob
        if (!doc.preview.startsWith("blob:")) {
          const response = await axios.get(doc.preview, {
            responseType: "blob",
            withCredentials: true,
          });
          const blob = new Blob([response.data], { type: response.data.type });
          blobUrl = URL.createObjectURL(blob);
          fileName =
            doc.fileName ||
            response.headers["content-disposition"]?.match(
              /filename="(.+)"/
            )?.[1] ||
            "document";
        }

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL if it was created
        if (blobUrl !== doc.preview) {
          URL.revokeObjectURL(blobUrl);
        }
      } catch (error) {
        console.error("Error downloading file:", error);
        setAlert({
          open: true,
          message: t(`Failed to download document: ${error.message}`),
          severity: "error",
        });
      }
    }
  };

  const handleSubmitDocuments = async (id) => {
    if (!id) {
      setAlert({
        open: true,
        message: t(`${entityType} ID is required to upload documents`),
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      for (const doc of documents) {
        if (doc.file && !doc.isUploaded && doc.type) {
          const formData = new FormData();
          formData.append("image", doc.file);

          if (doc.isExisting && doc.fileId) {
            try {
              await axios.delete(`${config.baseURL}/upload/image`, {
                params: { fileId: doc.fileId },
                withCredentials: true,
              });
              console.log(
                `Successfully deleted document with ID: ${doc.fileId}`
              );
            } catch (deleteError) {
              console.warn("Failed to delete old document:", deleteError);
            }
          }

          const response = await axios.post(
            `${config.baseURL}/upload/image?name=${encodeURIComponent(
              doc.type || "Other"
            )}&associateEntityId=${id}&associatedWith=${entityType}`,
            formData,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          const updatedDoc = {
            ...doc,
            isUploaded: true,
            isExisting: true,
            needsReplacement: false,
            fileName: response.data.location.split("/").pop(),
            fileId: response.data.id,
            preview: `${
              config.baseURL
            }/upload/image?fileName=${encodeURIComponent(
              response.data.location
            )}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          const updatedDocs = documents
            .map((d) => (d.id === doc.id ? updatedDoc : d))
            .filter((d) => d.type !== doc.type || d.id === doc.id);

          setDocuments(updatedDocs);
          setPropDocuments(updatedDocs.filter((d) => d.type !== ""));
        }
      }

      setAlert({
        open: true,
        message: t("Documents uploaded successfully"),
        severity: "success",
      });
      if (canAddDocument()) {
        handleAddDocument();
      }
    } catch (error) {
      console.error(`Error uploading ${entityType} documents:`, error);
      setAlert({
        open: true,
        message: t(
          `Failed to upload documents: ${
            error.response?.data?.error || error.message
          }`
        ),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async (isDetailsFormValid) => {
    if (!isDetailsFormValid) {
      setAlert({
        open: true,
        message: t("Please correct all validation errors before submitting"),
        severity: "error",
      });
      return;
    }

    const allTouched = {};
    documents.forEach((doc) => {
      allTouched[doc.id] = { type: true, file: true };
    });
    setTouched(allTouched);

    // Check if any document has errors
    const hasDocumentErrors = documents.some((doc) => {
      const docErrors = validateDocument(doc);
      return Object.keys(docErrors).length > 0;
    });

    if (hasDocumentErrors) {
      setAlert({
        open: true,
        message: t(
          "Please complete all document uploads and select types before saving"
        ),
        severity: "error",
      });
      return;
    }

    const isValid = validateAllDocuments();
    if (!isValid) {
      setAlert({
        open: true,
        message: t("Please correct all document validation errors"),
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onSubmit();
      const currentEntityId = result || entityId;

      if (!currentEntityId) {
        setAlert({
          open: true,
          message: t(`Failed to save ${entityType.toLowerCase()} details`),
          severity: "error",
        });
        setIsSubmitting(false);
        return;
      }

      const docsToUpload = documents.filter(
        (doc) => doc.file && !doc.isUploaded && doc.type
      );
      if (docsToUpload.length > 0) {
        await handleSubmitDocuments(currentEntityId);
      }

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error(`Error during ${entityType} save process:`, error);
      setAlert({
        open: true,
        message: t(
          `Failed to save: ${error.response?.data?.error || error.message}`
        ),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    documents,
    isLoading,
    isSubmitting,
    alert,
    errors,
    touched,
    confirmModal,
    setAlert,
    setTouched,
    canAddDocument,
    getRemainingOtherDocs,
    getAvailableDocTypes,
    handleAddDocument,
    handleRemoveDocument,
    handleCancelRemove,
    handleConfirmRemove,
    removeDocumentById,
    handleDocTypeChange,
    handleFileUpload,
    handleDownload,
    handleSubmitDocuments,
    handleSave,
  };
};
